import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import ImageDragAndDropQuizPreview from "../../features/previews/image_drag_and_drop_quiz/ImageDragAndDropQuizPreview";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { GetNewGuid } from "../../helpers/GuidHelper";
import { Actor } from "../Actor";
import { requiredGlobalQuizProperties } from "../GlobalQuizProperty";
import { Prop } from "../Prop";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";

export interface IImageDragAndDropQuizImage {
  id: string;
  assetId: string;
}

export interface IImageDragAndDropQuizDropZone {
  id: string;
  correctImageId?: string;
  xPos: number;
  yPos: number;
  xSize: number;
  ySize: number;
}

export interface IImageDragAndDropQuiz extends Interaction {
  title: string;
  imageAssetId?: string;
  images: IImageDragAndDropQuizImage[];
  dropZones: IImageDragAndDropQuizDropZone[];
}

export const IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES = 5;

const requiredImageDragAndDropQuizProperties = [...requiredGlobalQuizProperties];

const createDropZone = (
  correctImageId: string | undefined,
  xPos: number,
  yPos: number,
): IImageDragAndDropQuizDropZone => ({
  id: GetNewGuid(),
  correctImageId,
  xPos,
  yPos,
  xSize: 0.18,
  ySize: 0.14,
});

const migrateDropZoneGeometry = (
  dropZone: IImageDragAndDropQuizDropZone & {
    correctLabelId?: string;
    text?: string;
  },
  index: number,
): IImageDragAndDropQuizDropZone => {
  const {
    text: _legacyText,
    correctLabelId: _legacyCorrectLabelId,
    ...rest
  } = dropZone;

  return {
    ...rest,
    correctImageId: dropZone.correctImageId ?? dropZone.correctLabelId,
    xPos: dropZone.xPos ?? (index % 2 === 0 ? 0.4 : 0.6),
    yPos: dropZone.yPos ?? 0.5,
    xSize: dropZone.xSize ?? 0.18,
    ySize: dropZone.ySize ?? 0.14,
  };
};

export const imageDragAndDropQuizInteraction =
  (): InteractionFactoryEntity => {
    const component = <ImageDragAndDropQuizPreview />;

    const create = (
      getKeys: (amount: number) => string[],
    ): IImageDragAndDropQuiz => {
      const keys = getKeys(1);
      const firstImageId = GetNewGuid();
      const secondImageId = GetNewGuid();

      return {
        id: GetNewGuid(),
        name: "Image Drag And Drop Quiz",
        type: InteractionType.ImageDragAndDropQuiz,
        title: keys[0],
        imageAssetId: "",
        images: [
          { id: firstImageId, assetId: "" },
          { id: secondImageId, assetId: "" },
        ],
        dropZones: [
          createDropZone(firstImageId, 0.4, 0.5),
          createDropZone(secondImageId, 0.6, 0.5),
        ],
        properties: [...requiredImageDragAndDropQuizProperties],
      };
    };

    const migrate = (
      from: number,
      to: number,
      interaction: Interaction,
    ): IImageDragAndDropQuiz => {
      const casted = interaction as IImageDragAndDropQuiz;

      if (casted.properties === undefined) {
        console.log(
          "[Image Drag And Drop Quiz] migrated " +
            requiredImageDragAndDropQuizProperties.length +
            " properties",
        );
        return {
          ...casted,
          imageAssetId: casted.imageAssetId ?? "",
          images: casted.images ?? [],
          dropZones: (casted.dropZones ?? []).map(migrateDropZoneGeometry),
          properties: [...requiredImageDragAndDropQuizProperties],
        };
      }

      const newProperties = GetMigratedProperties(
        requiredImageDragAndDropQuizProperties,
        casted.properties,
      );

      console.log(
        "[Image Drag And Drop Quiz] migrated " +
          (newProperties.length - casted.properties.length) +
          " properties",
      );

      return {
        ...casted,
        imageAssetId: casted.imageAssetId ?? "",
        images: casted.images ?? [],
        dropZones: (casted.dropZones ?? []).map(migrateDropZoneGeometry),
        properties: newProperties,
      };
    };

    const copy = (
      interaction: Interaction,
      getKeys: (amount: number) => string[],
      replaceKey: (from: string, to: string) => void,
      getAnswerArray: (
        originalAnswers: IAnswer[],
        newKeys: string[],
      ) => IAnswer[],
    ): IImageDragAndDropQuiz => {
      const quiz = interaction as IImageDragAndDropQuiz;
      const newTitleKey = getKeys(1)[0];
      replaceKey(quiz.title, newTitleKey);

      const copiedImages = quiz.images.map((image) => ({
        id: GetNewGuid(),
        assetId: image.assetId,
        originalId: image.id,
      }));

      const copiedImageIdByOriginalId = new Map(
        copiedImages.map((image) => [image.originalId, image.id]),
      );

      const copiedDropZones = quiz.dropZones.map((dropZone) => ({
        id: GetNewGuid(),
        xPos: dropZone.xPos,
        yPos: dropZone.yPos,
        xSize: dropZone.xSize,
        ySize: dropZone.ySize,
        correctImageId: dropZone.correctImageId
          ? copiedImageIdByOriginalId.get(dropZone.correctImageId)
          : undefined,
      }));

      return {
        id: GetNewGuid(),
        name: interaction.name,
        type: InteractionType.ImageDragAndDropQuiz,
        title: newTitleKey,
        imageAssetId: quiz.imageAssetId ?? "",
        images: copiedImages.map(({ originalId, ...image }) => image),
        dropZones: copiedDropZones.map(migrateDropZoneGeometry),
        properties: quiz.properties
          ? GetMigratedProperties(
              requiredImageDragAndDropQuizProperties,
              quiz.properties,
            )
          : [...requiredImageDragAndDropQuizProperties],
      };
    };

    const validate = (
      interaction: Interaction,
      getValue: (key: string) => string,
      take: Take,
      actorPresets: Actor[],
    ): string[] => {
      const quiz = interaction as IImageDragAndDropQuiz;
      const warnings: string[] = [];

      ValidationHelper.ValidateKey(quiz.title, getValue, warnings, "Title");
      ValidationHelper.ValidateAssetId(
        quiz.imageAssetId ?? "",
        warnings,
        "Image",
      );

      return warnings;
    };

    const filterType = "Image Drag And Drop Quiz";

    const filter = (
      interaction: Interaction,
      takeName: string,
      getMatchingResults: (
        searchableStrings: string[],
        takeName: string,
        interaction: Interaction,
        filterType: string,
      ) => SearchResultData[],
    ): SearchResultData[] => {
      const quiz = interaction as IImageDragAndDropQuiz;
      const searchableStrings = [
        quiz.name,
        quiz.title,
        quiz.imageAssetId ?? "",
        ...quiz.images.map((image) => image.assetId),
      ];

      return getMatchingResults(
        searchableStrings,
        takeName,
        interaction,
        filterType,
      );
    };

    const validateErrors = (
      interaction: Interaction,
      parentTake: Take,
      currentTake: Take,
      actorPresets: Actor[],
      allPropPresets: Prop[],
      localizedStringRecords: LocalizedStringRecord,
    ): string[] => {
      const quiz = interaction as IImageDragAndDropQuiz;
      const errors: string[] = [];
      const imageIds = new Set(quiz.images.map((image) => image.id));
      const usedImageIds = new Set<string>();
      const duplicateImageIds = new Set<string>();

      if (quiz.images.length === 0) {
        errors.push("Quiz must have at least one image");
      }

      if (quiz.images.length > IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES) {
        errors.push(
          `Quiz can have at most ${IMAGE_DRAG_AND_DROP_QUIZ_MAX_IMAGES} images`,
        );
      }

      quiz.images.forEach((image, index) => {
        ValidationHelper.ValidateAssetId(
          image.assetId ?? "",
          errors,
          `Image ${index + 1}`,
        );
      });

      if (quiz.dropZones.length === 0) {
        errors.push("Quiz must have at least one drop zone");
      }

      quiz.dropZones.forEach((dropZone, index) => {
        if (!dropZone.correctImageId) {
          return;
        }

        if (!imageIds.has(dropZone.correctImageId)) {
          errors.push(`Drop zone ${index + 1} is mapped to a missing image`);
          return;
        }

        if (usedImageIds.has(dropZone.correctImageId)) {
          duplicateImageIds.add(dropZone.correctImageId);
          return;
        }

        usedImageIds.add(dropZone.correctImageId);
      });

      duplicateImageIds.forEach((imageId) => {
        const imageIndex = quiz.images.findIndex((image) => image.id === imageId);
        errors.push(`Image ${imageIndex + 1} is mapped to more than one drop zone`);
      });

      return errors;
    };

    return {
      View: component,
      FilterType: filterType,
      Create: create,
      Migrate: migrate,
      Copy: copy,
      Validate: validate,
      Filter: filter,
      ValidateErrors: validateErrors,
    };
  };
